/**
 * useCanonicalDrift — the ONE drift decision, in the one place a component may read it.
 *
 * TASK-723 deleted `AssetAllocation.rebalance_needed`: it was a second drift implementation
 * (asset-class granularity, a hardcoded 5% band) that disagreed with the calculation behind
 * the `portfolio_drift_breach` Radar signal. `GET /api/portfolio/allocation/{clientId}` now
 * omits the key on the happy path and sets it to `null` when the risk profile is incomplete.
 *
 * Two surfaces were never rewired and still read it (review 2026-07-29): `InsightsTab` and
 * `PriorityActions`. `allocation?.rebalance_needed` is now permanently falsy there, so the
 * drift row silently stopped appearing — and `PriorityActions` filled the gap with
 * "All compliance metrics within range", turning a removed analysis into a clean bill of
 * health. That is the exact inversion this epic exists to remove.
 *
 * The canonical verdict is tri-state and MUST stay tri-state:
 *
 *     true   — assessed, and at least one slot is outside the client's own tolerance band
 *     false  — assessed, and every slot is inside it
 *     null   — NOT ASSESSED. Never render this as "no rebalancing needed".
 *
 * Built on `useDrift` so it shares the ['client-drift', clientId] React Query cache with the
 * summoned canvas tiles and an open client dashboard — one request, one answer, no surface
 * able to hold a different one.
 */

import { useDrift } from './useDrift';

/** What the canonical endpoint returns under `drift`. Mirrors `portfolioApiService.getDrift`. */
export interface CanonicalDriftSlot {
  current: number;
  target: number;
  drift: number;
  breach?: boolean;
  asset_class?: string | null;
  sub_asset_class?: string | null;
}

export interface CanonicalDrift {
  assessment_status?: 'assessed' | 'incomplete';
  assessed_at?: string;
  calculation_version?: number;
  tolerance_pct?: number;
  needs_rebalancing: boolean | null;
  breached_slots?: string[] | null;
  message?: string;
  drift_by_asset_class?: Record<string, CanonicalDriftSlot>;
  /**
   * TASK-863: the portfolio total the percentages above were computed against, in CHF.
   *
   * Multiply a slot's `current` by anything else and the two disagree: `useClientLevelData`'s
   * `portfolioValue` is a par sum of `market_value` in whatever currency the custodian
   * delivered, while these percentages are converted. On a mixed-currency book that product is
   * wrong in both units — it was self-consistent only while both sides were par.
   */
  total_portfolio_value?: number;
  /** Value in a currency with no CHF rate, per currency. Non-empty means no verdict was made. */
  unconvertible_by_ccy?: Record<string, number>;
  has_unconvertible?: boolean;
}

/**
 * `incomplete` and `failed` are kept apart deliberately. "We cannot assess this client
 * because their risk profile has no section 7" is an answer the RM can act on; "the drift
 * service errored" is not. Collapsing them is how a data gap becomes indistinguishable from
 * an outage — and how both become "fine".
 */
export type CanonicalDriftState = 'loading' | 'ready' | 'incomplete' | 'failed';

export interface CanonicalDriftResult {
  drift: CanonicalDrift | null;
  state: CanonicalDriftState;
  /** Verbatim server detail for the incomplete/failed states; null when there is nothing to say. */
  note: string | null;
  /** The canonical verdict. `null` means NOT ASSESSED — never "no". */
  needsRebalancing: boolean | null;
  /** The breached sub-asset-class keys. Empty when nothing breached OR nothing was assessed. */
  breachedSlots: string[];
  /** True while the answer is unknown for any reason — the guard against a positive verdict. */
  unassessed: boolean;
}

export function useCanonicalDrift(clientId?: string | null): CanonicalDriftResult {
  const query = useDrift(clientId ?? '');

  if (!clientId) {
    return {
      drift: null,
      state: 'incomplete',
      note: 'No client selected.',
      needsRebalancing: null,
      breachedSlots: [],
      unassessed: true,
    };
  }

  if (query.isError) {
    // The endpoint answers 422 `risk_profile_incomplete` when section 7 is missing. That is
    // the correct answer, not a failure — and it must render as "cannot assess", never as a
    // verdict (TASK-722's rule).
    const err = query.error as any;
    const detail = err?.response?.data?.detail;
    const incomplete =
      err?.response?.status === 422 || detail?.error === 'risk_profile_incomplete';
    return {
      drift: null,
      state: incomplete ? 'incomplete' : 'failed',
      note: detail?.message ?? err?.message ?? null,
      needsRebalancing: null,
      breachedSlots: [],
      unassessed: true,
    };
  }

  const drift = (query.data?.drift ?? null) as CanonicalDrift | null;

  if (!drift) {
    // A 200 with no `drift` object is a broken contract, not a pending request. Reporting it
    // as "loading" would leave the surface waiting for ever on an answer that already came.
    return {
      drift: null,
      state: query.isSuccess ? 'failed' : 'loading',
      note: query.isSuccess ? 'The drift response carried no assessment.' : null,
      needsRebalancing: null,
      breachedSlots: [],
      unassessed: true,
    };
  }

  if (drift.assessment_status === 'incomplete') {
    return {
      drift,
      state: 'incomplete',
      note: drift.message ?? null,
      needsRebalancing: null,
      breachedSlots: [],
      unassessed: true,
    };
  }

  // An assessed result whose verdict is still null is a contradiction the server should not
  // produce — but if it does, "unknown" wins over "fine". Absence of a verdict is never a
  // negative verdict.
  const verdict = drift.needs_rebalancing ?? null;
  return {
    drift,
    state: 'ready',
    note: null,
    needsRebalancing: verdict,
    breachedSlots: drift.breached_slots ?? [],
    unassessed: verdict === null,
  };
}

export default useCanonicalDrift;
